/**
 * The checked form of focusGridRow's reveal filter, for server-side grids. It
 * applies the filter, waits for the grid's answer, and puts the previous
 * filter model back when nothing matched, so following a link to a deleted
 * or out-of-window entity doesn't leave the user on an empty grid stripped of
 * their own filters.
 *
 * ag-grid delivers listeners asynchronously (helios never sets
 * suppressAsyncEvents), so by the time a modelUpdated arrives anything may
 * have changed the filters. The answer only counts while the grid still
 * carries the model this applied; otherwise the follow is abandoned and
 * nothing is restored, so a filter the user set in between survives. Set
 * filter values compare as sets: External Transactions writes its `status`
 * filter back from its checkboxes after any filter change, with the same
 * values in a different order.
 *
 * A reloading server-side store shows a stub row, and a failed load is a stub
 * with failedLoad set, which is why failedLoad is checked before stub.
 */
import { Events, GridApi } from '@ag-grid-community/core'
import {
  focusGridRow,
  IdFilterModel,
  selectAndFlashRow,
} from './focus-grid-row'

export type RevealOutcome =
  | 'focused'
  | 'revealed'
  | 'notFound'
  /** Nothing matched, but the grid's data isn't complete yet; filters restored. */
  | 'notReady'
  | 'loadFailed'
  | 'abandoned'

export type FilterModel = Record<string, unknown>

export interface RevealOptions {
  hasRowNodeId: boolean
  idFilter: IdFilterModel
  /** Other column filters to apply with the id filter, e.g. to widen a default status filter. */
  alongside?: FilterModel
  /** Restore to this instead of the current model: the pre-follow filters of a reveal this one replaced. */
  previousFilterModel?: FilterModel
  /** An empty answer before this is true proves nothing, so it's reported as notReady. */
  isComplete?: () => boolean
}

export interface PendingReveal {
  /** The filters from before the follow; what a not-found result restores. */
  readonly previousFilterModel: FilterModel
  readonly outcome: Promise<RevealOutcome>
  readonly isPending: () => boolean
  cancel(): void
  /** Cancel, and put the previous filters back if the grid still carries the ones this applied. */
  withdraw(): void
}

export function revealGridRow(
  api: GridApi,
  idColId: string,
  id: string,
  options: RevealOptions,
): PendingReveal {
  const previousFilterModel =
    options.previousFilterModel ?? api.getFilterModel()
  if (focusGridRow(api, idColId, id, options.hasRowNodeId, false)) {
    return {
      previousFilterModel,
      outcome: Promise.resolve('focused'),
      isPending: () => false,
      cancel: () => undefined,
      withdraw: () => undefined,
    }
  }

  let open = true
  let settle: (outcome: RevealOutcome) => void = () => undefined
  const outcome = new Promise<RevealOutcome>(resolve => (settle = resolve))
  let appliedModel = ''
  const currentModel = () =>
    JSON.stringify(canonical(api.getFilterModel() ?? {}))

  const finish = (result: RevealOutcome) => {
    if (!open) return
    open = false
    api.removeEventListener(Events.EVENT_MODEL_UPDATED, onModelUpdated)
    settle(result)
  }
  const restore = () => {
    api.setFilterModel(previousFilterModel)
    api.onFilterChanged()
  }
  const onModelUpdated = () => {
    if (!open || !appliedModel) return
    if (currentModel() !== appliedModel) {
      finish('abandoned')
      return
    }
    const count = api.getDisplayedRowCount()
    const first = count > 0 ? api.getDisplayedRowAtIndex(0) : undefined
    if (first?.failedLoad) {
      restore()
      finish('loadFailed')
      return
    }
    if (first?.stub) return
    if (count === 0) {
      restore()
      finish(options.isComplete?.() === false ? 'notReady' : 'notFound')
      return
    }
    if (count === 1 && first) selectAndFlashRow(api, first)
    finish('revealed')
  }

  // Registered before the filter is applied, so the update it causes is seen.
  api.addEventListener(Events.EVENT_MODEL_UPDATED, onModelUpdated)
  api.setFilterModel({ ...options.alongside, [idColId]: options.idFilter(id) })
  api.onFilterChanged()
  appliedModel = currentModel()

  return {
    previousFilterModel,
    outcome,
    isPending: () => open,
    cancel: () => finish('abandoned'),
    withdraw: () => {
      if (!open) return
      const untouched = currentModel() === appliedModel
      finish('abandoned')
      if (untouched) restore()
    },
  }
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical)
  if (value == null || typeof value !== 'object') return value
  const record = value as Record<string, unknown>
  return Object.keys(record)
    .sort()
    .reduce<Record<string, unknown>>((out, key) => {
      const entry = record[key]
      out[key] =
        key === 'values' && Array.isArray(entry)
          ? entry.map(String).sort()
          : canonical(entry)
      return out
    }, {})
}
